import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { CurrencyUnitSelector } from 'components/BusinessToolForm/components/CurrencyUnitSelector/CurrencyUnitSelector';

import { BalanceSheet } from './components/BalanceSheet/BalanceSheet';
import {
  IOpeningBalanceSheetsValuesType,
  openingBalanceSheetsOptionsToComponentMap,
  useGetOpeningBalanceSheetsTabs,
} from './utils/form';
import { FinancialPlanChipSelector } from '../components/FinancialPlanChipSelector';
import { ActiveTabContextProvider, useTabChangeContext } from '../hooks/useTabChangeContext';

import styles from './OpeningBalanceSheets.module.scss';

const OpeningBalanceSheetsContent: FC = () => {
  const { t } = useTranslation();
  const OPENING_BALANCE_SHEETS_TABS = useGetOpeningBalanceSheetsTabs();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
  );

  const { activeTab, onTabChange } = useTabChangeContext();

  const Component = openingBalanceSheetsOptionsToComponentMap[activeTab.value as IOpeningBalanceSheetsValuesType];

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('financialPlan.title')}
          badgeText={t('financialPlan.openingBalanceSheets.title')}
        />
      </FormSectionCard>

      <BalanceSheet />

      <FormSectionCard>
        <Box className={styles.wrapper}>
          <FinancialPlanChipSelector
            options={OPENING_BALANCE_SHEETS_TABS}
            selected={activeTab}
            onChange={onTabChange}
            wrapWithSectionCard={false}
          />
          <CurrencyUnitSelector />
        </Box>
      </FormSectionCard>

      <Component />
    </>
  );
};

export const OpeningBalanceSheets: FC = () => {
  const OPENING_BALANCE_SHEETS_TABS = useGetOpeningBalanceSheetsTabs();
  return (
    <ActiveTabContextProvider initialValue={OPENING_BALANCE_SHEETS_TABS[0]}>
      <OpeningBalanceSheetsContent />
    </ActiveTabContextProvider>
  );
};
